import React, { useState, useEffect } from "react";
import isEqual from "lodash/isEqual";

// components
import Dropdown, { DropdownProps } from "@/shared/Dropdown";
import Button from "@/shared/Button";
import { InputNumber } from "@/shared/Input";

// assets
import styles from "@/assets/styles/components/space-dropdown.module.scss";


interface BudgetRange {
  min: number | null;
  max: number | null;
}

export interface IBudgetRangeDropdown extends Omit<DropdownProps, "onChange" | "value"> {
  onChange?: (value: BudgetRange) => void;
  value?: BudgetRange;
  currencySymbol: string;
  className?: string;
}

const DEFAULT_VALUES = { min: null, max: null };

function BudgetRangeDropdown({ onChange, value, currencySymbol, className = "", ...rest }: IBudgetRangeDropdown) {
  // needed to avoid changing the value during rerendering
  const [prevValue, setPrevValue] = useState(value);
  const [changeValue, setChangeValue] = useState(value || DEFAULT_VALUES);

  useEffect(() => {
    if (isEqual(prevValue, value)) {
      return;
    }

    if (typeof value !== "undefined") {
      setChangeValue({
        min: value.min ?? DEFAULT_VALUES.min,
        max: value.max ?? DEFAULT_VALUES.max,
      });
      setPrevValue(value);
    }
  }, [value]);

  const { min, max } = changeValue;
  const isFullValues = typeof min === "number" && typeof max === "number";
  const isSomeValues = typeof min === "number" || typeof max === "number";

  const handelChange = (newValueObject: BudgetRange) => {
    setChangeValue(newValueObject);
  };

  const onOpenChange = (visible: boolean) => {

    if (!visible && (!isFullValues) && !value) {
      setChangeValue(DEFAULT_VALUES);
    }

    if (!visible && isSomeValues) {
      onChange && onChange(changeValue as BudgetRange)
    }
  };

  const renderValue = () => {
    if (!isFullValues && !value) {
      return null;
    }

    return { label: [changeValue.min, changeValue.max].filter(item => !isNaN(item as number) && typeof item === "number").join(" - ") };
  };

  return (
    <Dropdown
      placeholder="Budget range"
      className={className}
      rootClassName={styles["locl-space-dropdown"]}
      onOpenChange={onOpenChange}
      value={renderValue() as DropdownProps["value"]}
      dropdownRender={() => (
        <div className={`locl-space-dropdown__menu ${styles["locl-space-dropdown__menu_column"]}`} data-testid="locl-space-dropdown-budget">
          <div className={styles["locl-space-dropdown__inputs-group"]}>
            <InputNumber
              fullWidth
              placeholder={`${currencySymbol} Min`}
              value={changeValue.min}
              onChange={e => handelChange({ ...changeValue, min: e } as BudgetRange)}
              data-testid="locl-space-dropdown-budget-input-min"
            />
            <InputNumber
              fullWidth
              placeholder={`${currencySymbol} Max`}
              value={changeValue.max}
              onChange={e => handelChange({ ...changeValue, max: e } as BudgetRange)}
              data-testid="locl-space-dropdown-budget-input-max"
            />
          </div>
          <Button fullWidth onClick={() => onChange && onChange(changeValue as BudgetRange)} data-testid="locl-space-dropdown-budget-button-set">
            Set Budget
          </Button>
        </div>
      )}
      {...rest}
    />
  );
}

export default BudgetRangeDropdown;
